import {
  Component,
  ErrorHandler,
  inject,
  input,
  signal,
  SimpleChange,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import { LocalStorageService } from '@core/common-legacy';
import { SkillDetails } from '@core/positions-and-skills-types';
import { BoxComponent, IconComponent, RtTooltip } from '@core/user-interface-legacy';

import { JobSkillRatingsComponent } from '../job-skill-ratings/job-skill-ratings.component';
import { JobSkillScaleRatingComponent } from '../job-skill-scale-rating/job-skill-scale-rating.component';
import { JobSkillSummaryBadgeComponent } from '../job-skill-summary-badge/job-skill-summary-badge.component';

@Component({
  selector: 'rt-job-skill-summary',
  templateUrl: './job-skill-summary.component.html',
  styleUrls: ['./job-skill-summary.component.less'],
  imports: [
    BoxComponent,
    IconComponent,
    RtTooltip,
    JobSkillSummaryBadgeComponent,
    JobSkillScaleRatingComponent,
    JobSkillRatingsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.collapsed]': 'isCollapsed()',
    '[class.expanded]': '!isCollapsed()',
  },
})
export class JobSkillSummaryComponent {
  private static readonly COLLAPSE_STATE_KEY = 'rt-collapsible-card';
  private static readonly COLLAPSE_KEY_REGISTER: Set<string> = new Set();

  private readonly localStorageService = inject(LocalStorageService);
  private readonly errorHandler = inject(ErrorHandler);

  public readonly uniqKey = input.required<string>();
  public readonly skill = input.required<SkillDetails>();

  public isCollapsed = signal(false);

  public ngOnInit(): void {
    this.setInitialState();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.updateUniqKey(changes?.uniqKey);
  }

  public ngOnDestroy(): void {
    this.unregisterUniqKey(this.uniqKey());
  }

  public onClick(): void {
    this.isCollapsed.set(!this.isCollapsed());
    this.saveCollapseState();
  }

  public saveCollapseState(): void {
    if (!this.uniqKey()) {
      return;
    }

    this.localStorageService.set(this.buildUniqKey(), this.isCollapsed());
  }

  public toggle(): void {
    this.onClick();
  }

  private buildUniqKey(): string {
    return `${JobSkillSummaryComponent.COLLAPSE_STATE_KEY}:${this.uniqKey()}`;
  }

  private validateAndRegisterUniqKey(): void | never {
    const uniqKey = this.uniqKey();
    if (!uniqKey) {
      return;
    }

    if (JobSkillSummaryComponent.COLLAPSE_KEY_REGISTER.has(uniqKey)) {
      this.errorHandler.handleError(new Error(`uniqKey "${uniqKey}" is not unique`));
    } else {
      JobSkillSummaryComponent.COLLAPSE_KEY_REGISTER.add(uniqKey);
    }
  }

  private unregisterUniqKey(key: string): void {
    if (!key) {
      return;
    }

    JobSkillSummaryComponent.COLLAPSE_KEY_REGISTER.delete(key);
  }

  private updateUniqKey(keyChange?: SimpleChange): void {
    if (
      !keyChange ||
      !keyChange.currentValue ||
      keyChange.currentValue === keyChange.previousValue
    ) {
      return;
    }

    this.unregisterUniqKey(keyChange.previousValue);
    this.validateAndRegisterUniqKey();
  }

  private setInitialState(): void {
    this.isCollapsed.set(this.localStorageService.get(this.buildUniqKey(), true));
  }
}
